import { JobStatus, ConnectProto } from "@racoon/api";
import { getEnumName } from "@racoon/lib";

type AnyJobStatus = JobStatus | ConnectProto.JobStatus | null | undefined;

/**
 * The status's proto name from either form: an adapted row carries the name (`JobStatus`),
 * a raw gRPC job numbers its status (`ConnectProto.JobStatus`).
 */
const statusName = (status: AnyJobStatus): string | undefined =>
  typeof status === "number"
    ? getEnumName(ConnectProto.JobStatusSchema, status)
    : (status ?? undefined);

// A job that already reached a terminal state is a closed record — editing it
// is not allowed (neither from the id link nor from the "Действие" menu).
const NON_EDITABLE_STATUS_NAMES: ReadonlySet<string> = new Set([
  "JOB_STATUS_COMPLETED",
  "JOB_STATUS_FORCE_COMPLETED",
  "JOB_STATUS_CANCELLED",
]);

export const isJobEditable = (status: AnyJobStatus): boolean => {
  if (status === undefined || status === null) return true;
  const name = statusName(status);
  return !name || !NON_EDITABLE_STATUS_NAMES.has(name);
};

/** A Невывоз: never routed itself — only re-issued, as a new attempt at its order. */
export const isJobNotCompleted = (status: AnyJobStatus): boolean =>
  statusName(status) === "JOB_STATUS_NOT_COMPLETED";

/** The orgs UpdateJob checks access against; a missing id is left out, not sent as 0. */
export const jobOrgIds = (job: ConnectProto.Job) => ({
  serviceProviderId: job.serviceProviderId || undefined,
  serviceReceiverId: job.serviceReceiverId || undefined,
});

/** The UpdateJob that cancels `job`, for a job on no route (CancelJob refuses those). */
export const cancelledJobUpdate = (job: ConnectProto.Job) => ({
  id: job.id,
  status: ConnectProto.JobStatus.CANCELLED,
  ...jobOrgIds(job),
});

/**
 * Putting a job on a trip only makes sense while it is still unassigned — once
 * it is on a route (or past that) it already has a trip, so both route actions
 * are limited to `JOB_STATUS_CREATED`. A Невывоз is the exception: it goes on a
 * trip as a re-issued attempt at its order, once the dispatcher agrees
 * (~/shared/hooks/use-reissue-expired-jobs) — but only its order's current attempt
 * is re-issued, which the status alone does not tell: a list of every attempt has
 * to check that too (the registry does, through `one_per_order`).
 */
export const isJobRoutable = (status: AnyJobStatus): boolean =>
  statusName(status) === "JOB_STATUS_CREATED" || isJobNotCompleted(status);

/** Nothing more happens to such a job; the backend refuses to list it as unfinished work. */
export const isFinalJobStatus = (status: ConnectProto.JobStatus) =>
  status === ConnectProto.JobStatus.COMPLETED ||
  status === ConnectProto.JobStatus.CANCELLED ||
  status === ConnectProto.JobStatus.FORCE_COMPLETED ||
  status === ConnectProto.JobStatus.NOT_COMPLETED;
